import {
  maybeParseZonedDateTime,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import {
  enumAddressType,
  FieldsSelection,
  Service,
} from '@rematter/frontend-lib-api-client';

import { ServiceRecord } from '../../types';
import { SERVICE_FRAGMENT } from '../@shared-fragments/serviceFragment';

type ServiceSelection = FieldsSelection<Service, typeof SERVICE_FRAGMENT>;

function getServiceCoordinates(service: ServiceSelection) {
  const coords =
    service.address?.coordinates ??
    service.accountAddress?.address?.coordinates ??
    service.facility?.address?.coordinates;
  if (!coords) return undefined;
  return {
    latitude: Number(coords.latitude),
    longitude: Number(coords.longitude),
  };
}

function getEffectiveAddress(
  service: ServiceSelection,
): ServiceRecord['address'] {
  if (service.address?.streetAddress) {
    return {
      streetAddress: service.address.streetAddress,
      city: service.address.city,
      state: service.address.state,
      postalCode: service.address.postalCode,
      country: service.address.country,
      addressType: service.address.addressType ?? enumAddressType.Standard,
      isMailingAddress: service.address.isMailingAddress ?? false,
    };
  }
  const fedAddress =
    service.accountAddress?.address ?? service.facility?.address;
  if (fedAddress?.streetAddress) {
    return {
      streetAddress: fedAddress.streetAddress,
      city: fedAddress.city,
      state: fedAddress.state,
      postalCode: fedAddress.postalCode,
      country: fedAddress.country,
      addressType: enumAddressType.Standard,
      isMailingAddress: false,
    };
  }
  return undefined;
}

export const mapService = (service: ServiceSelection): ServiceRecord => ({
  id: service.id,
  serviceTypeId: service.serviceType?.id,
  serviceTypeName: service.serviceType?.name,
  status: service.status,
  documentNumber: service.documentNumber,
  rank: service.rank,
  priority: service.priority,
  routeId: service.routeId ?? undefined,
  routePosition: service.routePosition ?? undefined,
  scheduledWindowStart: parseZonedDateTime(service.scheduledWindowStart ?? ''),
  scheduledWindowEnd: parseZonedDateTime(service.scheduledWindowEnd ?? ''),
  startedAt: maybeParseZonedDateTime(service.startedAt),
  completedAt: maybeParseZonedDateTime(service.completedAt),
  assignedAt: maybeParseZonedDateTime(service.assignedAt),
  estimatedArrival: maybeParseZonedDateTime(service.estimatedArrival),
  internalNotes: service.internalNotes ?? undefined,
  externalNotes: service.externalNotes ?? undefined,
  driverNotes: service.driverNotes ?? undefined,
  driver: service.driver ?? undefined,
  driverId: service.driverId ?? undefined,
  haulerId: service.haulerId ?? undefined,
  truck: service.truck ?? undefined,
  truckId: service.truckId ?? undefined,
  trailer: service.trailer ?? undefined,
  trailerId: service.trailerId ?? undefined,
  facilityGroupId: service.facilityGroupId ?? undefined,
  facilityId: service.facilityId ?? undefined,
  destinationFacilityId: service.destinationFacilityId ?? undefined,
  accountId: service.accountId ?? undefined,
  accountName: service.account?.name ?? undefined,
  accountAddressId: service.accountAddressId ?? undefined,
  accountAddressName: service.accountAddress?.name ?? undefined,
  address: getEffectiveAddress(service),
  coordinates: getServiceCoordinates(service),
  facilityCoordinates: service.facility?.address?.coordinates
    ? {
        latitude: Number(service.facility.address.coordinates.latitude),
        longitude: Number(service.facility.address.coordinates.longitude),
      }
    : undefined,
  accountAddressCoordinates: service.accountAddress?.address?.coordinates
    ? {
        latitude: Number(service.accountAddress.address.coordinates.latitude),
        longitude: Number(service.accountAddress.address.coordinates.longitude),
      }
    : undefined,
  assetQuantities: service.actions?.map((action) => ({
    assetType: action.assetType
      ? {
          id: action.assetType.id,
          name: action.assetType.name,
          capacity: action.assetType.capacity ?? undefined,
        }
      : undefined,
    quantity: action.quantity ?? 1,
    assetGroup: action.assetGroup ?? undefined,
  })),
  timestamps: {
    createdAt: parseZonedDateTime(service.timestamps.createdAt),
    updatedAt: parseZonedDateTime(service.timestamps.updatedAt),
  },
  relationshipType: service.relationshipType ?? undefined,
});
